/**
 * Lienzo de previsualizacion: renderiza al ancho real del sitio y lo escala
 * para que quepa en el panel.
 *
 * POR QUE EXISTE. La previsualizacion montaba los componentes reales, pero
 * dentro del ancho del panel de administracion — mas angosto que el sitio. Los
 * componentes se reflowean a ese ancho, asi que el editor veia una version
 * distinta de la que se publica: otro largo de linea, otro punto de corte de la
 * foto, y en pantallas chicas hasta el layout movil. Una previsualizacion que
 * reflowea no previsualiza.
 *
 * COMO. El contenido se dibuja SIEMPRE a `ancho` px (1320, el maximo del sitio)
 * y se reduce con `transform: scale()`. El escalado no reflowea: la caja sigue
 * midiendo 1320 y los componentes deciden su layout con ese ancho, exactamente
 * como en el sitio. Solo cambia el tamano en pantalla.
 *
 * `zoom` no sirve aunque parezca mas simple: reflowea, que es justo lo que hay
 * que evitar.
 *
 * La altura del hueco se ajusta a la del contenido ya escalado, para que no
 * quede un vacio debajo ni se solape con lo que sigue.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Ancho maximo del sitio publico: el `max-width: 1320px` de sus contenedores. */
export const ANCHO_SITIO = 1320;

/**
 * Piso de la escala.
 *
 * Sin piso, un panel de 420px daba 0.32: el cuerpo de 16px se dibujaba a 5px y
 * la previsualizacion dejaba de ser legible justo cuando mas falta hace mirarla.
 * Por debajo de este valor el lienzo deja de encoger y se desplaza en
 * horizontal: se ve menos ancho de sitio a la vez, pero se ve.
 */
export const ESCALA_MINIMA = 0.45;

export default function Lienzo({ ancho = ANCHO_SITIO, children }) {
  const hueco = useRef(null);
  const contenido = useRef(null);
  const [escala, setEscala] = useState(1);
  const [alto, setAlto] = useState(0);
  const [desborda, setDesborda] = useState(false);

  const medir = useCallback(() => {
    const disponible = hueco.current?.clientWidth;
    if (!disponible) return;
    // Nunca se agranda: a 1320 o mas se ve a tamano real.
    const ideal = Math.min(1, disponible / ancho);
    const nueva = Math.max(ESCALA_MINIMA, ideal);
    setEscala(nueva);
    setDesborda(nueva > ideal);
    const altoReal = contenido.current?.scrollHeight;
    if (altoReal) setAlto(altoReal * nueva);
  }, [ancho]);

  useEffect(() => {
    medir();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', medir);
      return () => window.removeEventListener('resize', medir);
    }
    // Dos observadores: el hueco cambia al redimensionar la ventana o al abrir
    // el menu lateral; el contenido cambia cuando el editor escribe.
    const obsHueco = new ResizeObserver(medir);
    const obsContenido = new ResizeObserver(medir);
    if (hueco.current) obsHueco.observe(hueco.current);
    if (contenido.current) obsContenido.observe(contenido.current);
    return () => {
      obsHueco.disconnect();
      obsContenido.disconnect();
    };
  }, [medir]);

  return (
    <div
      ref={hueco}
      style={{
        // El alto lo fija el contenido escalado; el 1 evita un salto en el
        // primer render, antes de la primera medicion.
        height: alto || 1,
        // Al llegar al piso el contenido es mas ancho que el hueco: se desplaza
        // en horizontal en vez de recortarse en silencio.
        overflowX: desborda ? 'auto' : 'hidden',
        overflowY: 'hidden',
        // Borde de hoja: deja claro donde termina el sitio y empieza el panel.
        borderRadius: 12,
        border: '1px solid var(--yool-border, #E0E0E0)',
        background: 'var(--white, #fff)',
      }}
    >
      <div
        ref={contenido}
        style={{
          width: ancho,
          transform: `scale(${escala})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}
